/**
 * Environment utility functions for Flexibook UI
 */

/**
 * Get the booking URL prefix based on the current environment
 * @returns {string} The booking URL prefix (with trailing slash)
 */
export const getBookingUrlPrefix = () => {
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3000/book/";
  } else {
    return (
      process.env.REACT_APP_BOOKING_URL_PREFIX ||
      "https://app.flexibook.ca/book/"
    );
  }
};

/**
 * Get the full shareable booking URL for a given slug.
 * Combines getBookingUrlPrefix() with the slug so callers never need to
 * know whether the prefix carries a /book/ path (flexibook.ca) or not
 * (book.anyspot.io).
 * @param {string} slug
 * @returns {string|null} The full booking URL, or null if no slug
 */
export const getBookingUrl = (slug) => {
  if (!slug) return null;
  return `${getBookingUrlPrefix()}${slug}`;
};

/**
 * Whether to expose the public booking page at the bare /:slug root path
 * (in addition to /book/:slug, which is always registered).
 *
 * Auto-enabled when the runtime hostname starts with `book.` (e.g.
 * book.anyspot.io). The literal dot is intentional — `book-staging.anyspot.io`
 * is NOT auto-enabled. A single shared bundle can therefore serve both
 * app.* and book.* subdomains from one Render service without registering
 * `/:slug` on app.* (which would otherwise capture /dashboard and other
 * admin paths as slugs).
 *
 * To test locally, add `127.0.0.1 book.localhost` to /etc/hosts and visit
 * http://book.localhost:3000/<slug>.
 *
 * @returns {boolean}
 */
export const isBookingRootRouteEnabled = () => {
  if (typeof window === "undefined") return false;
  return window.location?.hostname?.startsWith("book.") === true;
};

/**
 * Get the marketing/landing site URL for the current build target.
 * @returns {string} The marketing URL (with trailing slash)
 */
export const getMarketingUrl = () => {
  return process.env.REACT_APP_MARKETING_URL || "https://flexibook.ca/";
};

/**
 * Build the platform-specific one-click subscribe links for an ICS calendar
 * feed, plus the raw feed URL itself.
 *
 * `apple` uses the `webcal://` scheme (one-tap native subscribe on
 * iOS/macOS; has no native handler on Android). `google` routes through
 * Google Calendar's web subscribe endpoint, which is the only reliable
 * cross-platform (including Android) path. `outlook` opens the Outlook web
 * "add from web" subscribe flow.
 *
 * @param {string} feedUrl - Full HTTPS feed URL, e.g.
 *   `https://api.flexibook.ca/api/public/calendar/<token>.ics`
 * @returns {{apple: string, google: string, outlook: string, raw: string}|null}
 *   Null if no feed URL is provided.
 */
export const getCalendarSubscribeLinks = (feedUrl) => {
  if (!feedUrl) return null;

  const webcalUrl = feedUrl.replace(/^https?:\/\//, "webcal://");

  return {
    apple: webcalUrl,
    google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(
      webcalUrl
    )}`,
    outlook: `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(
      webcalUrl
    )}`,
    raw: feedUrl,
  };
};

/**
 * Detect which calendar app to lead with as the primary one-click subscribe
 * action, based on the visitor's device. iOS/macOS Safari support the
 * `webcal://` scheme natively; everyone else (including Android, where
 * `webcal://` has no handler) is safest routed to Google Calendar's web
 * subscribe flow.
 *
 * Used only to choose which button is *primary* — every option (Apple,
 * Google, Outlook, raw URL) always stays reachable via the "Use a different
 * calendar" disclosure.
 *
 * @returns {"apple"|"google"|"other"}
 */
export const detectCalendarPlatform = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return "other";
  }

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isMac =
    /Macintosh/.test(ua) &&
    typeof document !== "undefined" &&
    "ontouchend" in document === false;
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Android/.test(ua);

  if (isIOS || (isMac && isSafari)) {
    return "apple";
  }

  // Android and everything else default to Google — the safe cross-platform
  // choice (webcal:// has no native handler on Android).
  return "google";
};

/**
 * Check if the current environment is development
 * @returns {boolean} True if development environment
 */
export const isDevelopment = () => {
  return process.env.NODE_ENV === "development";
};

/**
 * Check if the current environment is production
 * @returns {boolean} True if production environment
 */
export const isProduction = () => {
  return process.env.NODE_ENV === "production";
};
